import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { Add, PrintOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { callPostApi } from "../../function/ApiCallFunction";
import { adminPost } from "../admin/adminApi";
import { clubPost } from "../club/clubApi";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { formatAmount } from "../../include/money";
import { indiaDate } from "../../include/indiaDate";
import { canAdd } from "../../include/staffAccess";
import physicalFront from "../../assets/physicalcard.jpg";
import physicalBack from "../../assets/physicalback.jpg";

const STATUSES = [
  { id: "requested", label: "Requested" },
  { id: "confirm", label: "Confirm" },
  { id: "printing", label: "Printing" },
  { id: "delivered", label: "Delivered" },
];

function statusLabel(value) {
  return STATUSES.find((item) => item.id === value)?.label || value;
}

function paths(source) {
  if (source === "club") return { list: "/club/print", add: "/club/print/new" };
  if (source === "admin") return { list: "/console/print-requests", add: "/console/print-requests", print: "/console/print-physical" };
  return { list: "/printphysicalcard", add: "/printphysicalcard/new" };
}

function postFor(source, storeId, clubId, mod, data = {}) {
  if (source === "club") return clubPost(mod, { club_id: clubId, ...data });
  if (source === "admin") return adminPost(mod, data);
  return callPostApi("store-draw", { mod, data_arr: { store_id: storeId, ...data } });
}

function introText(source) {
  if (source === "admin") return "Store and club loyalty-card print requests. Print the physical card here, then change status as cards move from requested to delivered.";
  if (source === "club") return "Request printed loyalty cards from this club. Wallet is cut per card when you submit.";
  return "Request printed loyalty cards from this store. Wallet is cut per card when you submit.";
}

function CardFaces({ compact = false }) {
  const face = (src, label) => (
    <Box sx={{ minWidth: compact ? 168 : 0, flex: compact ? "0 0 auto" : { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: consoleColors.muted, mb: 0.6 }}>{label}</Typography>
      <Box
        component="img"
        src={src}
        alt={label}
        sx={{
          width: compact ? 168 : "100%",
          maxWidth: compact ? 168 : 420,
          height: "auto",
          aspectRatio: "16 / 9",
          objectFit: "contain",
          background: "#f8fafc",
          borderRadius: 1.5,
          border: `1px solid ${consoleColors.line}`,
          display: "block",
        }}
      />
    </Box>
  );
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
      {face(physicalFront, "Front")}
      {face(physicalBack, "Back")}
    </Box>
  );
}

export default function PrintRequests({ source = "store" }) {
  const navigate = useNavigate();
  const { loginData, clubData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [wallet, setWallet] = useState(null);
  const route = paths(source);
  const ready = source === "admin" || (source === "club" ? Boolean(clubId) : Boolean(storeId));

  const load = useCallback(async () => {
    if (!ready) return;
    const result = await postFor(source, storeId, clubId, "PRINT_REQUEST_LIST", source === "admin" && filter ? { status: filter } : {});
    if (result.status === "200") {
      setRows(result.data?.requests || []);
      if (result.data?.wallet_amount != null) setWallet(Number(result.data.wallet_amount));
    }
  }, [ready, source, storeId, clubId, filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (row, status) => {
    const result = await adminPost("PRINT_REQUEST_STATUS", { request_id: row.request_id, status });
    if (result.status === "200") {
      toast.success(result.data?.message || "Status updated");
      load();
    } else toast.info(result.status_message || result.data?.error || "Could not update status");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ maxWidth: 640 }}>
          <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>{introText(source)}</Typography>
          {wallet != null && source !== "admin" && (
            <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.4 }}>Wallet ₹{formatAmount(wallet)}</Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {source === "admin" && (
            <TextField select size="small" label="Status" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
            </TextField>
          )}
          {source === "admin" && (
            <Button variant="contained" sx={{ ...primaryButtonSx, whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => navigate(route.print)}>
              <PrintOutlined sx={{ mr: 0.5, fontSize: 18 }} /> Print physical card
            </Button>
          )}
          {source !== "admin" && canAdd(source === "club" ? clubData : loginData) && (
            <Button variant="contained" sx={{ ...primaryButtonSx, whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => navigate(route.add)}><Add sx={{ mr: 0.5 }} /> Request Printed Cards</Button>
          )}
        </Box>
      </Box>

      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 750, mb: 1.2 }}>Physical card</Typography>
        <CardFaces />
      </Box>

      {rows.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No print requests yet.
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {rows.map((row) => (
          <Box key={row.request_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <CardFaces compact />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{row.draw_title || "Loyalty Card"}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>
                {row.qty} cards · ₹{formatAmount(row.unit_price)} each · Wallet cut ₹{formatAmount(row.amount)} · {indiaDate(row.date_added)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.ink, mt: 0.3 }}>
                {source === "admin" ? `${row.source_label} · ` : ""}{statusLabel(row.status)}
              </Typography>
            </Box>
            {source === "admin" ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`${route.print}?qty=${row.qty}${row.store_id ? `&store=${encodeURIComponent(row.store_id)}` : ""}`)}>Print</Button>
                <TextField select size="small" label="Status" value={row.status} onChange={(e) => changeStatus(row, e.target.value)} sx={{ minWidth: 160 }}>
                  {STATUSES.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                </TextField>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{statusLabel(row.status)}</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function PrintRequestForm({ source = "store" }) {
  const navigate = useNavigate();
  const { loginData, setLoginData, clubData, setClubData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;
  const route = paths(source);
  const [price, setPrice] = useState(0);
  const [qtyText, setQtyText] = useState("1");
  const [saving, setSaving] = useState(false);
  const ready = source === "club" ? Boolean(clubId) : Boolean(storeId);

  useEffect(() => {
    if (!ready) return;
    postFor(source, storeId, clubId, "PRINT_PRICE").then((result) => {
      if (result.status === "200") setPrice(Number(result.data?.print_price || result.data?.print_one_side || 0));
    });
  }, [ready, source, storeId, clubId]);

  const qty = Math.max(0, Math.floor(Number(qtyText) || 0));
  const amount = Number((price * qty).toFixed(2));

  const save = async () => {
    setSaving(true);
    try {
      const result = await postFor(source, storeId, clubId, "PRINT_REQUEST_CREATE", { qty });
      if (result.status === "200") {
        if (source === "store" && result.data?.wallet_amount != null) {
          const updated = { ...loginData, store_info: { ...loginData.store_info, wallet_amount: result.data.wallet_amount } };
          setLoginData(updated);
        }
        if (source === "club" && result.data?.wallet_amount != null) {
          const updated = { ...clubData, club_info: { ...clubData.club_info, wallet_amount: result.data.wallet_amount } };
          setClubData(updated);
          sessionStorage.setItem("clubData", JSON.stringify(updated));
        }
        toast.success(`The total ₹${formatAmount(amount)} has been deducted from the wallet for the cards. Delivery is expected within 10 days.`);
        navigate(route.list);
      } else toast.info(result.status_message || result.data?.error || "Could not submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate(route.list)}>Back to print requests</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>Request Printed Cards</Typography>
        <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>
          {source === "club" ? "Request printed loyalty cards from this club." : "Request printed loyalty cards from this store."} Choose how many cards. Wallet cut ₹{formatAmount(price)} per card.
        </Typography>
        <CardFaces />
        <TextField label="Number of cards" type="number" value={qtyText} onChange={(e) => setQtyText(e.target.value)} inputProps={{ min: 1, step: 1 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 750 }}>Wallet cut ₹{formatAmount(amount)}</Typography>
          {qty >= 1 && (
            <Typography sx={{ fontSize: 13.5, color: consoleColors.muted, mt: 0.5, lineHeight: 1.45 }}>
              The total ₹{formatAmount(amount)} has been deducted from the wallet for the cards. Delivery is expected within 10 days.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button sx={{ textTransform: "none" }} onClick={() => navigate(route.list)}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving || qty < 1} onClick={save}>{saving ? "Submitting..." : "Submit request"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
